import React from 'react';
import trim from 'lodash/trim';
import Grid from '@packages/systems/design-system/Grid';

import {
  withSystem,
  withMeta,
  Expressions,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionsAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';

import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TText,
  TRecord,
  TUnion,
  TEnum,
  TListOfElement,
  EText,
  TTypeConstructor,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {
  getNewElementId,
  getClosestFormElement,
  findElementDescendant,
} from '@packages/utilities/platform';
import {
  isPasswordPageForm,
  isFormButtonElement,
  isFormWrapper,
  isFormForm,
} from '@packages/utilities/elementTypeUtils';
import {
  selectSelectedElement,
  selectCurrentComponentRender,
} from '@packages/utilities/platform-selectors';
import {toStringId, toSafeFormId} from '@packages/systems/core/utils/HtmlUtils';
import {
  EMAIL_FORM_DEFAULT,
  TEMP_PASSWORD_STATE_PATH,
  ID_PATH,
  ATTR_NAME_PATH,
  ATTR_DATA_NAME_PATH,
  FORM_NAME_PATH,
} from '../constants';
import {TEMP_STATE_PATH} from '@packages/utilities/expression-path-constants';

import {FORM_SOURCE} from './useFormSource';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

import {SchemaSection} from '../SchemaSection/SchemaSection';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {SendToSection} from '@packages/systems/designer/forms/client';
const {getIn} = ContextualExpressions;

const selectMiniSettings = (state: DesignerStores) =>
  state.UiStore.miniSettingsFlags;

const selectComponents = (state: DesignerStores) =>
  state.DesignerStore.stagedComponents || state.DesignerStore.components;

const FormFormType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: {
    children: TListOfElement,
    Source: withMeta(
      {variant: 'menu'},
      TUnion(
        Object.keys(FORM_SOURCE).reduce<Record<string, any>>(
          (acc, key) => ({
            ...acc,
            // @ts-expect-error - TS2532 - Object is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
            [FORM_SOURCE[key].tag]: FORM_SOURCE[key].type,
          }),
          {}
        )
      )
    ),
    temp: TRecord({
      state: withSystem(
        {
          label: 'State',
          defaultValue: 'normal',
          isLocalizable: true,
        },
        TEnum({normal: 'Normal', success: 'Success', error: 'Error'})
      ),
      'password-state': withSystem(
        {
          label: 'State',
          defaultValue: 'normal',
          isLocalizable: true,
        },
        TEnum({normal: 'Default', error: 'Wrong Password'})
      ),
    }),
    form: TRecord({
      type: withSystem(
        {
          private: true,
          defaultValue: 'form',
        },
        TText
      ),
      name: withMeta(
        {placeholder: 'e.g. Contact Form'},
        withSystem(
          {label: 'Name', defaultValue: 'Email Form', isLocalizable: true},
          TTypeConstructor(
            qualify(Plugins.name('Basic'), typeName('OnBlurText'))
          )
        )
      ),
    }),
    attr: TRecord({
      id: withSystem(
        {
          private: true,
          defaultValue: EMAIL_FORM_DEFAULT,
          label: 'ID',
          isBindable: false,
          isLocalizable: true,
        },
        TTypeConstructor(
          qualify(Plugins.name('Basic'), typeName('IdTextInput'))
        )
      ),
      name: withSystem(
        {private: true, defaultValue: EMAIL_FORM_DEFAULT, isLocalizable: true},
        TText
      ),
      'data-name': withSystem(
        {private: true, defaultValue: 'Email Form', isLocalizable: true},
        TText
      ),
      redirect: withMeta(
        {placeholder: 'e.g. /success'},
        withSystem(
          {label: 'Redirect URL', defaultValue: '', isLocalizable: true},
          TTypeConstructor(
            qualify(Plugins.name('Basic'), typeName('OnBlurText'))
          )
        )
      ),
      'data-redirect': withSystem(
        {private: true, defaultValue: '', isLocalizable: true},
        TText
      ),
      action: withMeta(
        {placeholder: '(Webflow default)'},
        withSystem(
          {label: 'Action', defaultValue: '', isLocalizable: true},
          TTypeConstructor(
            qualify(Plugins.name('Basic'), typeName('OnBlurText'))
          )
        )
      ),
      method: withSystem(
        {label: 'Method', defaultValue: 'get', isLocalizable: true},
        TEnum({get: 'GET', post: 'POST'})
      ),
    }),
  },
  render: function RenderFormForm({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    const componentMap = useDesignerSelector(selectComponents);
    const selectedElement = useDesignerSelector(selectSelectedElement);
    const currentComponentRender = useDesignerSelector(
      selectCurrentComponentRender
    );
    const isFormButton =
      selectedElement && isFormButtonElement(selectedElement);
    const isPasswordPage =
      selectedElement && isPasswordPageForm(selectedElement);

    const onChangeAt = React.useCallback(
      (at: ExpressionPath) => extendExpressionEditorActionAtPath(dispatch)(at),
      [dispatch]
    );
    const onChangeName = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'exp' implicitly has an 'any' type.
      (exp) => {
        const currentNameFromExpr = getIn(expr, FORM_NAME_PATH);
        const currentName = currentNameFromExpr
          ? ContextualExpressions.getText(stripAnn(currentNameFromExpr))
          : EMAIL_FORM_DEFAULT;
        const name = Expressions.getText(exp.payload.newExpression) || '';
        const trimmedName = trim(name);
        const nameId = toStringId(trimmedName) || '';
        const nameFormId = `wf-form-${nameId}`;
        const nameSafeIdExpr = EText(toSafeFormId(name));
        const changes = [
          {key: FORM_NAME_PATH, newExpression: nameSafeIdExpr},
          {key: ATTR_DATA_NAME_PATH, newExpression: nameSafeIdExpr},
        ];
        const idNeedsUpdate = currentName && nameId !== toStringId(currentName);
        if (idNeedsUpdate) {
          const newId = getNewElementId(
            componentMap,
            nameFormId || EMAIL_FORM_DEFAULT
          );
          const newIdExp = EText(newId);
          changes.push(
            {key: ID_PATH, newExpression: newIdExp},
            {key: ATTR_NAME_PATH, newExpression: newIdExp}
          );
        }
        dispatch(updateExpressionsAction(changes));
      },
      [dispatch, expr, componentMap]
    );
    const onChangePassword = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'exp' implicitly has an 'any' type.
      (exp) => {
        const password = Expressions.getEnum(exp.payload.newExpression) || '';
        const passwordExpr = EEnum(password);
        dispatch(
          updateExpressionsAction([
            {key: TEMP_STATE_PATH, newExpression: passwordExpr},
            {key: TEMP_PASSWORD_STATE_PATH, newExpression: passwordExpr},
          ])
        );
      },
      [dispatch]
    );

    const expressionComponent = React.useCallback(
      (path: ExpressionPath) => {
        const exprAt = getIn(expr, path);
        const dispatchFn = new Map([
          [FORM_NAME_PATH, onChangeName],
          [TEMP_PASSWORD_STATE_PATH, onChangePassword],
        ]);
        const onDispatch = dispatchFn.get(path) || onChangeAt(path);
        if (!exprAt) {
          throw Error(
            `Failed to get expression at path: ${JSON.stringify(path)}`
          );
        }
        return <ExpressionEditor expression={exprAt} dispatch={onDispatch} />;
      },
      [expr, onChangeAt, onChangeName, onChangePassword]
    );

    const State = expressionComponent(TEMP_STATE_PATH);
    const Name = expressionComponent(FORM_NAME_PATH);

    const miniSettings = useDesignerSelector(selectMiniSettings);
    const isMiniSettingsOpen = Boolean(Object.keys(miniSettings || {}).length);

    const formFormElement = React.useMemo(() => {
      return (
        selectedElement &&
        currentComponentRender &&
        (isFormWrapper(selectedElement)
          ? findElementDescendant(selectedElement, isFormForm)
          : getClosestFormElement(currentComponentRender, selectedElement.id))
      );
    }, [selectedElement, currentComponentRender]);

    if (isPasswordPage) {
      const PasswordState = expressionComponent(TEMP_PASSWORD_STATE_PATH);
      return <>{PasswordState}</>;
    }

    const renderFormLevelSettings = () => (
      <>
        {formFormElement ? (
          <>
            <SchemaSection formFormElement={formFormElement} />
            <SendToSection
              expr={expr}
              expressionEditorDispatch={dispatch}
              isMiniSettingsOpen={isMiniSettingsOpen}
              formFormElement={formFormElement}
            />
          </>
        ) : null}
      </>
    );

    return (
      <Grid gridGap={2}>
        {!isFormButton ? (
          <>
            {State}
            {Name}
          </>
        ) : null}
        {renderFormLevelSettings()}
      </Grid>
    );
  },
});

export default FormFormType;
